import React, { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { compressImage } from '../utils/imageCompression'
import { supabaseClient } from '../config/supabase'
import toast from 'react-hot-toast'
import { Search, Edit2, Trash2, ChevronLeft, ChevronRight, Eye, Clock, CheckCircle, XCircle, UserCheck, LayoutGrid, LayoutList, User } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { useAdminScope } from '../hooks/useAdminScope'
import type { Department, Program, PendingAlumni } from '../types'
import { SkillsPicker } from '../components/SkillsPicker'
import {
  EMPLOYMENT_STATUS_OPTIONS, EMPLOYER_TYPE_OPTIONS, JOB_RELEVANCE_OPTIONS,
  SALARY_RANGE_OPTIONS, EMPLOYMENT_LENGTH_OPTIONS,
  isEmployed, toDbValue, toDisplayValue,
} from '../constants/profileOptions'
import { CareerTimeline } from '../components/CareerTimeline'
import { notifyAlumniApprovedEmail, notifyAlumniRejectedEmail } from '../utils/emailNotify'

const ManageAlumni: React.FC = () => {
  const location = useLocation()
  const { user, refreshUser } = useAuthStore()
  const { isSuperAdmin, allowedDepartments, loading: scopeLoading } = useAdminScope()
  const [activeTab, setActiveTab] = useState<'all' | 'pending'>(
    location.state?.tab === 'pending' ? 'pending' : 'all'
  )
  const [alumni, setAlumni] = useState<any[]>([])
  const [filteredAlumni, setFilteredAlumni] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedProgram, setSelectedProgram] = useState('all')
  const [selectedYear, setSelectedYear] = useState('all')
  const [selectedDepartment, setSelectedDepartment] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [programs, setPrograms] = useState<string[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedAlumni, setSelectedAlumni] = useState<Set<string>>(new Set())
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [currentAlumni, setCurrentAlumni] = useState<any>(null)
  const [editFormData, setEditFormData] = useState<any>({})
  const [saving, setSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string>('')
  const [departments, setDepartments] = useState<Department[]>([])
  const [allPrograms, setAllPrograms] = useState<Program[]>([])
  const [pendingAlumni, setPendingAlumni] = useState<PendingAlumni[]>([])
  const [pendingLoading, setPendingLoading] = useState(false)
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set())
  const [pendingSearch, setPendingSearch] = useState('')
  const [pendingSelectedYear, setPendingSelectedYear] = useState('all')
  const [pendingSelectedDepartment, setPendingSelectedDepartment] = useState('all')
  const [approveModal, setApproveModal] = useState<{ id: string; name: string } | null>(null)
  const [rejectModal, setRejectModal] = useState<{ id: string; name: string } | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [alumniView, setAlumniView] = useState<'table' | 'card'>('table')
  const itemsPerPage = 12
  const graduationYears = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: currentYear - 1900 + 1 }, (_, index) => currentYear - index)
  }, [])

  const availableYears = useMemo(() => {
    return Array.from(
      new Set(
        alumni
          .map((a: any) => a.alumni_profiles?.year_graduated)
          .filter((y: any) => y !== null && y !== undefined && y !== '')
          .map((y: any) => Number(y))
          .filter((y: number) => !Number.isNaN(y))
      )
    ).sort((a, b) => b - a)
  }, [alumni])

  const availableDepartments = useMemo(() => {
    return Array.from(
      new Set(
        alumni
          .map((a: any) => a.alumni_profiles?.department)
          .filter(Boolean)
      )
    ).sort((a, b) => String(a).localeCompare(String(b)))
  }, [alumni])

  // toDbValue, toDisplayValue, isEmployed imported from constants/profileOptions

  useEffect(() => {
    // Wait for admin scope to load before fetching data
    if (!scopeLoading) {
      fetchAlumni()
      fetchDepartmentsAndPrograms()
    }
  }, [isSuperAdmin, allowedDepartments, scopeLoading])

  useEffect(() => {
    // Wait for admin scope to load before fetching pending alumni
    if (!scopeLoading) {
      fetchPendingAlumni()
    }
  }, [isSuperAdmin, allowedDepartments, scopeLoading])

  useEffect(() => {
    filterAlumni()
  }, [searchQuery, selectedProgram, selectedYear, selectedDepartment, selectedStatus, alumni])

  const fetchAlumni = async () => {
    // Don't fetch if admin scope is still loading
    if (scopeLoading) return
    
    try {
      setLoading(true)
      
      // Fetch alumni with their profiles
      // Hint the specific FK to avoid PostgREST ambiguity if schema cache is stale
      let query = supabaseClient
        .from('users')
        .select(`
          id,
          email,
          full_name,
          created_at,
          alumni_profiles!inner (
            first_name,
            last_name,
            age,
            gender,
            year_graduated,
            program,
            department,
            current_employment_status,
            job_position,
            field_of_work,
            employer_type,
            job_relevance,
            salary_range,
            employment_length,
            unemployment_reasons,
            further_studies,
            skills_acquired,
            career_progression,
            job_search_methods,
            alumni_engagement_openness,
            profile_picture
          )
        `)
        .eq('role', 'alumni')
        .order('created_at', { ascending: false })

      // Only show approved alumni in the main list
      query = query.eq('alumni_profiles.approval_status', 'approved')

      // Department admins: filter to their departments only
      if (!isSuperAdmin && allowedDepartments && allowedDepartments.length > 0) {
        query = query.in('alumni_profiles.department', allowedDepartments)
      }

      const { data: alumniData, error } = await query

      if (error) throw error

      console.log('Fetched alumni data:', alumniData)
      if (alumniData && alumniData.length > 0) {
        console.log('First alumnus structure:', JSON.stringify(alumniData[0], null, 2))
      }

      // Extract unique programs for filter
      const uniquePrograms = Array.from(
        new Set(
          alumniData
            ?.map((a: any) => a.alumni_profiles?.program || a.alumni_profiles?.department)
            .filter(Boolean)
        )
      ) as string[]
      
      setPrograms(uniquePrograms)
      setAlumni(alumniData || [])
      setFilteredAlumni(alumniData || [])
    } catch (error: any) {
      console.error('Error fetching alumni:', error)
      toast.error('Failed to load alumni data')
    } finally {
      setLoading(false)
    }
  }

  const filterAlumni = () => {
    let filtered = [...alumni]

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter((a) => {
        const profile = a.alumni_profiles
        const profileName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.toLowerCase()
        const fullName = a.full_name?.toLowerCase() || ''
        const email = a.email?.toLowerCase() || ''
        const query = searchQuery.toLowerCase()
        return profileName.includes(query) || fullName.includes(query) || email.includes(query)
      })
    }

    // Apply program filter
    if (selectedProgram !== 'all') {
      filtered = filtered.filter((a) => {
        const profile = a.alumni_profiles
        return profile?.program === selectedProgram || profile?.department === selectedProgram
      })
    }

    // Apply graduation year filter
    if (selectedYear !== 'all') {
      filtered = filtered.filter((a) => {
        const profile = a.alumni_profiles
        return String(profile?.year_graduated || '') === selectedYear
      })
    }

    // Apply department filter (superadmin only)
    if (isSuperAdmin && selectedDepartment !== 'all') {
      filtered = filtered.filter((a) => {
        const profile = a.alumni_profiles
        return profile?.department === selectedDepartment
      })
    }

    // Apply employment status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter((a) => {
        const status = a.alumni_profiles?.current_employment_status
        return status === selectedStatus
      })
    }

    setFilteredAlumni(filtered)
    setCurrentPage(1) // Reset to first page when filtering
  }

  // Fetch departments and programs from database
  const fetchDepartmentsAndPrograms = async () => {
    try {
      // Fetch departments
      const { data: depts, error: deptsError } = await supabaseClient
        .from('departments')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })

      if (deptsError) throw deptsError

      // Fetch programs
      const { data: progs, error: progsError } = await supabaseClient
        .from('programs')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })

      if (progsError) throw progsError

      setDepartments(depts || [])
      setAllPrograms(progs || [])
    } catch (err: any) {
      console.error('Error fetching departments and programs:', err)
      toast.error('Failed to load departments and programs')
    }
  }

  const fetchPendingAlumni = async () => {
    // Don't fetch if admin scope is still loading
    if (scopeLoading) return
    
    try {
      setPendingLoading(true)

      let query = supabaseClient
        .from('alumni_profiles')
        .select('id, user_id, first_name, last_name, department, program, year_graduated, created_at')
        .eq('approval_status', 'pending')
        .order('created_at', { ascending: true })

      // Department admins: filter to their departments only
      if (!isSuperAdmin && allowedDepartments && allowedDepartments.length > 0) {
        query = query.in('department', allowedDepartments)
      }

      const { data, error } = await query
      if (error) throw error

      // Fetch emails separately to avoid FK join ambiguity
      const userIds = (data || []).map((a: any) => a.user_id).filter(Boolean)
      let emailMap: Record<string, string> = {}
      if (userIds.length > 0) {
        const { data: users } = await supabaseClient
          .from('users')
          .select('id, email')
          .in('id', userIds)
        ;(users || []).forEach((u: any) => { emailMap[u.id] = u.email })
      }

      const result: PendingAlumni[] = (data || []).map((a: any) => ({
        id: a.id,
        user_id: a.user_id,
        first_name: a.first_name || '',
        last_name: a.last_name || '',
        email: emailMap[a.user_id] || '',
        department: a.department || '',
        program: a.program || '',
        year_graduated: a.year_graduated,
        created_at: a.created_at,
      }))

      setPendingAlumni(result)
    } catch (err: any) {
      console.error('Error fetching pending alumni:', err)
      toast.error('Failed to load pending alumni')
    } finally {
      setPendingLoading(false)
    }
  }

  const handleApprove = async (alumniId: string, alumniName: string) => {
    if (processingIds.has(alumniId)) return
    setApproveModal({ id: alumniId, name: alumniName })
  }

  const confirmApprove = async () => {
    if (!approveModal) return
    const { id: alumniId, name: alumniName } = approveModal
    const alumniUserInfo = pendingAlumni.find((a) => a.id === alumniId)
    setApproveModal(null)
    setProcessingIds((prev) => new Set(prev).add(alumniId))
    try {
      const { error } = await supabaseClient.rpc('approve_alumni', { alumni_profile_id: alumniId })
      if (error) throw error
      toast.success(`${alumniName} has been approved`)
      if (alumniUserInfo?.user_id) notifyAlumniApprovedEmail(alumniUserInfo.user_id)
      setPendingAlumni((prev) => prev.filter((a) => a.id !== alumniId))
    } catch (err: any) {
      toast.error('Failed to approve alumni: ' + (err.message || 'Unknown error'))
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev)
        next.delete(alumniId)
        return next
      })
    }
  }

  const handleReject = async (alumniId: string, alumniName: string) => {
    if (processingIds.has(alumniId)) return
    setRejectReason('')
    setRejectModal({ id: alumniId, name: alumniName })
  }

  const confirmReject = async () => {
    if (!rejectModal || !rejectReason.trim()) return
    const { id: alumniId, name: alumniName } = rejectModal
    const alumniUserInfo = pendingAlumni.find((a) => a.id === alumniId)
    setRejectModal(null)
    setProcessingIds((prev) => new Set(prev).add(alumniId))
    try {
      const { error } = await supabaseClient.rpc('reject_alumni', {
        alumni_profile_id: alumniId,
        reason: rejectReason.trim(),
      })
      if (error) throw error
      toast.success(`${alumniName}'s registration has been rejected`)
      if (alumniUserInfo?.user_id) notifyAlumniRejectedEmail(alumniUserInfo.user_id, rejectReason.trim())
      setPendingAlumni((prev) => prev.filter((a) => a.id !== alumniId))
    } catch (err: any) {
      toast.error('Failed to reject alumni: ' + (err.message || 'Unknown error'))
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev)
        next.delete(alumniId)
        return next
      })
    }
  }

  const filteredPendingAlumni = useMemo(() => {
    let filtered = [...pendingAlumni]

    if (pendingSearch.trim()) {
      const q = pendingSearch.toLowerCase()
      filtered = filtered.filter(
        (a) =>
          a.first_name.toLowerCase().includes(q) ||
          a.last_name.toLowerCase().includes(q) ||
          a.program?.toLowerCase().includes(q) ||
          a.department?.toLowerCase().includes(q)
      )
    }

    if (pendingSelectedYear !== 'all') {
      filtered = filtered.filter((a) => String(a.year_graduated || '') === pendingSelectedYear)
    }

    if (isSuperAdmin && pendingSelectedDepartment !== 'all') {
      filtered = filtered.filter((a) => a.department === pendingSelectedDepartment)
    }

    return filtered
  }, [pendingAlumni, pendingSearch, pendingSelectedYear, pendingSelectedDepartment, isSuperAdmin])

  const pendingAvailableYears = useMemo(() => {
    return Array.from(
      new Set(
        pendingAlumni
          .map((a) => a.year_graduated)
          .filter((y): y is number => y !== null && y !== undefined)
      )
    ).sort((a, b) => b - a)
  }, [pendingAlumni])

  const pendingAvailableDepartments = useMemo(() => {
    return Array.from(
      new Set(
        pendingAlumni
          .map((a) => a.department)
          .filter(Boolean)
      )
    ).sort((a, b) => String(a).localeCompare(String(b)))
  }, [pendingAlumni])

  // Get available programs based on selected department
  const getProgramsByDepartment = (departmentName: string): Program[] => {
    const dept = departments.find(d => d.name === departmentName)
    if (!dept) return []
    return allPrograms.filter(p => p.department_id === dept.id)
  }

  // Normalize program value: convert full name to code if needed
  const normalizeProgramValue = (programValue: string): string => {
    if (!programValue) return ''
    
    // Check if it's already a code (short format like "BSCS")
    const programByCode = allPrograms.find(p => p.code === programValue)
    if (programByCode) return programValue
    
    // Try to find by full name and return the code
    const programByName = allPrograms.find(p => p.full_name === programValue || p.name === programValue)
    if (programByName) return programByName.code || programByName.name
    
    // If not found, return as-is
    return programValue
  }

  const openEditModal = (alumnus: any) => {
    const profile = alumnus.alumni_profiles || {}
    
    // Convert database enum values to display values
    const employmentStatus = toDisplayValue(profile.current_employment_status || 'unemployed', 'employment_status')
    const employerType = toDisplayValue(profile.employer_type || '', 'employer_type')
    const jobRelevance = toDisplayValue(profile.job_relevance || '', 'job_relevance')
    const salaryRange = toDisplayValue(profile.salary_range || '', 'salary_range')
    const employmentLength = toDisplayValue(profile.employment_length || '', 'employment_length')
    
    setCurrentAlumni(alumnus)
    setEditFormData({
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      email: alumnus.email || '',
      age: profile.age || '',
      gender: profile.gender || '',
      year_graduated: profile.year_graduated || '',
      program: normalizeProgramValue(profile.program || ''),
      department: profile.department || '',
      current_employment_status: employmentStatus,
      job_position: profile.job_position || '',
      field_of_work: profile.field_of_work || '',
      employer_type: employerType,
      job_relevance: jobRelevance,
      salary_range: salaryRange,
      employment_length: employmentLength,
      unemployment_reasons: profile.unemployment_reasons || '',
      further_studies: profile.further_studies || [],
      skills_acquired: profile.skills_acquired || [],
      career_progression: profile.career_progression || [],
      job_search_methods: profile.job_search_methods || [],
      profile_picture: profile.profile_picture || '',
    })
    setIsEditing(false)
    setSelectedFile(null)
    setImagePreview(profile.profile_picture || '')
    setEditModalOpen(true)
  }

  const openDeleteModal = (alumnus: any) => {
    setCurrentAlumni(alumnus)
    setDeleteModalOpen(true)
  }

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Image size must be less than 5MB')
        return
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file')
        return
      }
      setSelectedFile(file)
      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const uploadProfilePicture = async (): Promise<string | null> => {
    if (!selectedFile || !currentAlumni) return null

    try {
      const compressed = await compressImage(selectedFile, { maxWidth: 400, maxHeight: 400, quality: 0.85 })
      const fileName = `${currentAlumni.id}-${Date.now()}.jpg`
      const filePath = `profile-pictures/${fileName}`

      // Upload to Supabase storage
      const { error: uploadError } = await supabaseClient.storage
        .from('alumni-profiles')
        .upload(filePath, compressed, {
          upsert: true,
          contentType: 'image/jpeg'
        })

      if (uploadError) {
        console.error('Upload error:', uploadError)
        
        // Provide helpful error messages
        if (uploadError.message.includes('not found') || uploadError.message.includes('bucket')) {
          toast.error('Storage bucket not set up. Please create "alumni-profiles" bucket in Supabase Storage.')
        } else if (uploadError.message.includes('policy')) {
          toast.error('Permission denied. Please check storage policies in Supabase.')
        } else {
          toast.error(`Upload failed: ${uploadError.message}`)
        }
        throw uploadError
      }

      // Get public URL
      const { data } = supabaseClient.storage
        .from('alumni-profiles')
        .getPublicUrl(filePath)

      return data.publicUrl
    } catch (error: any) {
      console.error('Error uploading image:', error)
      return null
    }
  }

  const handleUpdateAlumni = async () => {
    if (!currentAlumni) return

    try {
      setSaving(true)

      const firstName = (editFormData.first_name || '').trim()
      const lastName = (editFormData.last_name || '').trim()

      if (!firstName) {
        toast.error('First name is required')
        setSaving(false)
        return
      }

      if (!lastName) {
        toast.error('Last name is required')
        setSaving(false)
        return
      }

      const trimmedFullName = `${firstName} ${lastName}`

      // Handle email change (superadmin only)
      const originalEmail = currentAlumni.email || ''
      const newEmail = (editFormData.email || '').trim().toLowerCase()
      const emailChanged = isSuperAdmin && newEmail && newEmail !== originalEmail.toLowerCase()

      if (emailChanged) {
        const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
        if (!emailRegex.test(newEmail)) {
          toast.error('Please enter a valid email address')
          setSaving(false)
          return
        }
        const { error: emailError } = await supabaseClient.rpc('admin_update_alumni_email', {
          target_user_id: currentAlumni.id,
          new_email: newEmail,
        })
        if (emailError) {
          toast.error(emailError.message || 'Failed to update email')
          setSaving(false)
          return
        }
      }

      // Upload profile picture if a new one was selected
      let profilePictureUrl = editFormData.profile_picture
      if (selectedFile) {
        const uploadedUrl = await uploadProfilePicture()
        if (uploadedUrl) {
          profilePictureUrl = uploadedUrl
        }
      }

      // Parse full name into first and last name
      // Update user table with full name
      const { error: userError } = await supabaseClient
        .from('users')
        .update({
          full_name: trimmedFullName,
        })
        .eq('id', currentAlumni.id)

      if (userError) {
        console.error('User update error:', userError)
        throw userError
      }

      // Update alumni profile
      const { error: profileError } = await supabaseClient
        .from('alumni_profiles')
        .update({
          first_name: firstName,
          last_name: lastName,
          age: editFormData.age ? parseInt(editFormData.age) : null,
          gender: editFormData.gender || null,
          current_employment_status: toDbValue(editFormData.current_employment_status, 'employment_status'),
          job_position: editFormData.job_position || null,
          field_of_work: editFormData.field_of_work || null,
          employer_type: toDbValue(editFormData.employer_type, 'employer_type'),
          job_relevance: toDbValue(editFormData.job_relevance, 'job_relevance'),
          salary_range: toDbValue(editFormData.salary_range || '', 'salary_range'),
          employment_length: toDbValue(editFormData.employment_length || '', 'employment_length'),
          unemployment_reasons: editFormData.unemployment_reasons || null,
          further_studies: editFormData.further_studies || null,
          skills_acquired: editFormData.skills_acquired || null,
          career_progression: editFormData.career_progression || null,
          job_search_methods: editFormData.job_search_methods || null,
          profile_picture: profilePictureUrl || null,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', currentAlumni.id)

      if (profileError) {
        console.error('Profile update error:', profileError)
        throw profileError
      }

      // Refresh the data first
      await fetchAlumni()
      
      // If the updated alumni is the current user, refresh their session data
      if (user && currentAlumni.id === user.id) {
        await refreshUser()
      }
      
      toast.success('Alumni updated successfully')
      setEditModalOpen(false)
      setCurrentAlumni(null)
      setIsEditing(false)
      setSelectedFile(null)
      setImagePreview('')
    } catch (error: any) {
      console.error('Error updating alumni:', error)
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to update alumni'
      
      if (error.message) {
        // Check for specific database constraint errors
        if (error.message.includes('alumni_profiles_year_graduated_check')) {
          const currentYear = new Date().getFullYear()
          errorMessage = `Invalid year. Please enter a year between 1900 and ${currentYear}`
        } else if (error.message.includes('duplicate key') || error.message.includes('unique constraint')) {
          errorMessage = 'This email is already in use by another user'
        } else if (error.message.includes('violates check constraint')) {
          errorMessage = 'Invalid data entered. Please check all fields and try again'
        } else if (error.message.includes('permission denied') || error.message.includes('RLS')) {
          errorMessage = 'You do not have permission to update this alumni'
        } else {
          errorMessage = error.message
        }
      }
      
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!currentAlumni) return

    try {
      setSaving(true)
      const { error } = await supabaseClient.from('users').delete().eq('id', currentAlumni.id)

      if (error) throw error

      toast.success('Alumni deleted successfully')
      setDeleteModalOpen(false)
      setCurrentAlumni(null)
      fetchAlumni()
    } catch (error: any) {
      console.error('Error deleting alumni:', error)
      toast.error('Failed to delete alumni')
    } finally {
      setSaving(false)
    }
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const currentPageAlumni = paginatedAlumni.map((a) => a.id)
      setSelectedAlumni(new Set(currentPageAlumni))
    } else {
      setSelectedAlumni(new Set())
    }
  }

  const handleSelectAlumni = (alumniId: string, checked: boolean) => {
    const newSelected = new Set(selectedAlumni)
    if (checked) {
      newSelected.add(alumniId)
    } else {
      newSelected.delete(alumniId)
    }
    setSelectedAlumni(newSelected)
  }

  const getStatusBadge = (status: string) => {
    const statusConfig: any = {
      employed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Employed' },
      'self-employed': { bg: 'bg-green-100', text: 'text-green-800', label: 'Self-employed' },
      unemployed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Unemployed' },
      studying: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Studying' },
      studying_employed: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Studying + Working' },
    }

    const config = statusConfig[status] || statusConfig.unemployed
    return (
      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    )
  }

  // Pagination
  const totalPages = Math.ceil(filteredAlumni.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedAlumni = filteredAlumni.slice(startIndex, endIndex)

  return (
    <div className="space-y-6">
      <div className="bg-blue-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden mb-6">
        <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
        <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />
        <div className="relative flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
            <UserCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Manage Alumni</h1>
            <p className="text-blue-200 text-sm mt-1">Review alumni records and process pending approvals.</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-5 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'all'
              ? 'bg-white border border-b-white border-gray-200 text-blue-600 -mb-px'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          All Alumni
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'pending'
              ? 'bg-white border border-b-white border-gray-200 text-blue-600 -mb-px'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Clock className="w-4 h-4" />
          Pending Approvals
          {pendingAlumni.length > 0 && (
            <span className="bg-yellow-400 text-gray-900 text-xs font-bold px-2 py-0.5 rounded-full">
              {pendingAlumni.length}
            </span>
          )}
        </button>
      </div>

      {/* Pending Approvals Tab */}
      {activeTab === 'pending' && (
        <div>
          {/* Search for pending */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 p-4">
            <div className="flex flex-col sm:flex-row gap-3 items-center">
              <div className="relative w-full sm:flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by name, program, or department..."
                  value={pendingSearch}
                  onChange={(e) => setPendingSearch(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div className="w-full sm:w-40">
                <select
                  value={pendingSelectedYear}
                  onChange={(e) => setPendingSelectedYear(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg"
                >
                  <option value="all">All Years</option>
                  {(pendingAvailableYears.length > 0 ? pendingAvailableYears : graduationYears).map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              {isSuperAdmin && (
                <div className="w-full sm:w-56">
                  <select
                    value={pendingSelectedDepartment}
                    onChange={(e) => setPendingSelectedDepartment(e.target.value)}
                    className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg"
                  >
                    <option value="all">All Departments</option>
                    {pendingAvailableDepartments.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {pendingLoading ? (
            <div className="text-center py-12 text-sm text-gray-500">Loading pending alumni...</div>
          ) : filteredPendingAlumni.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <UserCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No pending alumni registrations</p>
              <p className="text-sm text-gray-400 mt-1">New alumni who register will appear here for approval</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredPendingAlumni.map((alumnus) => {
                const isProcessing = processingIds.has(alumnus.id)
                const fullName = `${alumnus.first_name} ${alumnus.last_name}`.trim()
                return (
                  <div key={alumnus.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                    {/* Verification info */}
                    <div className="bg-blue-50 border-b border-blue-100 p-4">
                      <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">Verification Info</p>
                      <div className="space-y-1">
                        <p className="text-sm font-bold text-gray-900">{fullName}</p>
                        <p className="text-sm text-gray-700">{alumnus.department}</p>
                        <p className="text-sm text-gray-700">{alumnus.program}</p>
                        {alumnus.year_graduated && (
                          <p className="text-sm text-gray-700">Class of {alumnus.year_graduated}</p>
                        )}
                      </div>
                    </div>
                    {/* Secondary info */}
                    <div className="p-4">
                      <p className="text-xs text-gray-500 mb-3">{alumnus.email}</p>
                      <p className="text-xs text-gray-400 mb-4">
                        Registered {new Date(alumnus.created_at).toLocaleDateString()}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprove(alumnus.id, fullName)}
                          disabled={isProcessing}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(alumnus.id, fullName)}
                          disabled={isProcessing}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-red-100 text-red-700 text-sm font-medium rounded-lg hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* All Alumni Tab */}
      {activeTab === 'all' && (
      <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4 flex flex-col sm:flex-row gap-4 items-center">
          {/* Search Input */}
          <div className="relative flex-1 w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search alumni..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          {/* Program Filter */}
          <div className="w-full sm:w-64">
            <select
              value={selectedProgram}
              onChange={(e) => setSelectedProgram(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg"
            >
              <option value="all">All Programs</option>
              {programs.map((program) => (
                <option key={program} value={program}>
                  {program}
                </option>
              ))}
            </select>
          </div>

          {/* Year Filter */}
          <div className="w-full sm:w-40">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg"
            >
              <option value="all">All Years</option>
              {(availableYears.length > 0 ? availableYears : graduationYears).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Department Filter (Superadmin only) */}
          {isSuperAdmin && (
            <div className="w-full sm:w-56">
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg"
              >
                <option value="all">All Departments</option>
                {availableDepartments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Employment Status Filter */}
          <div className="w-full sm:w-48">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg"
            >
              <option value="all">All Statuses</option>
              <option value="employed">Employed</option>
              <option value="self-employed">Self-Employed</option>
              <option value="studying_employed">Studying + Working</option>
              <option value="unemployed">Unemployed</option>
              <option value="studying">Studying</option>
            </select>
          </div>

          {/* View toggle */}
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg flex-shrink-0">
            <button
              onClick={() => setAlumniView('table')}
              title="Table view"
              className={`p-1.5 rounded-md transition-colors ${
                alumniView === 'table' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <LayoutList className="w-4 h-4" />
            </button>
            <button
              onClick={() => setAlumniView('card')}
              title="Card view"
              className={`p-1.5 rounded-md transition-colors ${
                alumniView === 'card' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Alumni Card View */}
      {alumniView === 'card' && (
        <>
          {loading ? (
            <div className="text-center py-12 text-sm text-gray-500">Loading alumni...</div>
          ) : paginatedAlumni.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <p className="text-gray-500">No alumni found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {paginatedAlumni.map((alumnus) => {
                const profile = alumnus.alumni_profiles
                const fullName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
                const displayName = fullName || alumnus.full_name || alumnus.email
                const program = profile?.program || profile?.department || 'N/A'
                const year = profile?.year_graduated
                const status = profile?.current_employment_status || 'unemployed'
                return (
                  <div key={alumnus.id} className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col items-center text-center overflow-hidden">
                    {/* Thin blue top accent */}
                    <div className="w-full h-1 bg-blue-900 flex-shrink-0" />
                    <div className="flex flex-col items-center px-5 pt-5 pb-5 w-full">
                      {/* Avatar */}
                      <div className="w-16 h-16 rounded-full bg-blue-50 border-2 border-blue-100 flex items-center justify-center overflow-hidden flex-shrink-0 mb-3">
                        {profile?.profile_picture ? (
                          <img
                            src={profile.profile_picture}
                            alt={displayName}
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                          />
                        ) : (
                          <User className="w-7 h-7 text-blue-300" />
                        )}
                      </div>
                      <p className="font-bold text-gray-900 text-sm leading-tight">{displayName}</p>
                      <span className="text-xs text-blue-700 bg-blue-50 px-2.5 py-0.5 rounded-full mt-1.5">{program}</span>
                      {year && (
                        <span className="mt-1.5 text-xs font-semibold bg-yellow-400 text-gray-900 px-2.5 py-0.5 rounded-full">
                          Class of {year}
                        </span>
                      )}
                      <div className="mt-2">{getStatusBadge(status)}</div>
                      <p className="text-xs text-gray-400 mt-2 truncate w-full">{alumnus.email}</p>
                      <div className="flex gap-2 mt-4 w-full">
                        <button
                          onClick={() => openEditModal(alumnus)}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-900 text-white text-xs font-semibold rounded-xl hover:bg-blue-800 transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" /> View
                        </button>
                        {isSuperAdmin && (
                          <button
                            onClick={() => openDeleteModal(alumnus)}
                            className="p-2 text-red-400 hover:bg-red-50 rounded-xl border border-gray-200 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {/* Pagination for card view */}
          {!loading && filteredAlumni.length > itemsPerPage && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{startIndex + 1}</span>–<span className="font-medium">{Math.min(endIndex, filteredAlumni.length)}</span> of <span className="font-medium">{filteredAlumni.length}</span>
              </p>
              <div className="flex gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Alumni Table */}
      {alumniView === 'table' && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-8 px-3 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={paginatedAlumni.length > 0 && paginatedAlumni.every((a) => selectedAlumni.has(a.id))}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="w-48 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="w-40 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Program
                </th>
                <th className="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Year
                </th>
                <th className="w-28 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="w-36 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Field
                </th>
                <th className="w-28 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-3 py-12 text-center text-sm text-gray-500">
                    Loading alumni...
                  </td>
                </tr>
              ) : paginatedAlumni.length > 0 ? (
                paginatedAlumni.map((alumnus) => {
                  const profile = alumnus.alumni_profiles
                  console.log('Alumnus:', alumnus.id, 'Profile:', profile)
                  const fullName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
                  const displayName = fullName || alumnus.full_name || alumnus.email
                  const shouldShowEmail = displayName !== alumnus.email // Show email only if we're showing a name
                  const program = profile?.program || profile?.department || 'N/A'
                  const year = profile?.year_graduated || 'N/A'
                  const status = profile?.current_employment_status || 'unemployed'
                  const company = profile?.field_of_work || 'N/A'

                  return (
                    <tr key={alumnus.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedAlumni.has(alumnus.id)}
                          onChange={(e) => handleSelectAlumni(alumnus.id, e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-3 py-3 min-w-0">
                        <div className="flex items-center gap-2">
                          <img
                            src={profile?.profile_picture || '/default-avatar.svg'}
                            alt={displayName}
                            className="w-8 h-8 rounded-full object-cover shrink-0"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/default-avatar.svg'
                            }}
                          />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{displayName}</div>
                            {shouldShowEmail && <div className="text-xs text-gray-500 truncate">{alumnus.email}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-500 truncate" title={program}>{program}</td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">{year}</td>
                      <td className="px-3 py-3 whitespace-nowrap">{getStatusBadge(status)}</td>
                      <td className="px-3 py-3 text-sm text-gray-500 truncate" title={company}>{company}</td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditModal(alumnus)}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="View alumni profile"
                          >
                            <Eye className="w-4 h-4" />
                            <span className="text-sm font-medium">View</span>
                          </button>
                          {isSuperAdmin && (
                          <button
                            onClick={() => openDeleteModal(alumnus)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete alumni"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={7} className="px-3 py-12 text-center text-sm text-gray-500">
                    No alumni found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {!loading && filteredAlumni.length > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, filteredAlumni.length)}</span> of{' '}
                  <span className="font-medium">{filteredAlumni.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
      </>
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">View Alumni Profile</h2>
                <p className="text-sm text-gray-500 mt-1">{isEditing ? 'Editing mode enabled' : 'View mode - click Edit to make changes'}</p>
              </div>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
              )}
            </div>
            <div className="p-6 space-y-4">
              {/* Profile Picture Section */}
              <div className="flex items-center gap-4 pb-4 border-b border-gray-200">
                <img
                  src={imagePreview || '/default-avatar.svg'}
                  alt="Profile"
                  className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/default-avatar.svg'
                  }}
                />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Profile Picture</label>
                  {currentAlumni?.created_at && (
                    <p className="text-xs text-gray-500 mb-2">
                      Joined on {new Date(currentAlumni.created_at).toLocaleDateString('en-PH', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  )}
                  {isEditing ? (
                    <>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">Select an image file (max 5MB). Supported formats: JPG, PNG, GIF</p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-600">Click Edit to change profile picture</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editFormData.first_name || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                      isEditing ? 'focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-50 text-gray-700 cursor-not-allowed'
                    }`}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editFormData.last_name || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                      isEditing ? 'focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-50 text-gray-700 cursor-not-allowed'
                    }`}
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email{isSuperAdmin && isEditing && <span className="ml-1 text-xs text-blue-600 font-normal">(super admin only)</span>}
                </label>
                <input
                  type="email"
                  value={editFormData.email || ''}
                  onChange={isSuperAdmin && isEditing ? (e) => setEditFormData({ ...editFormData, email: e.target.value }) : undefined}
                  disabled={!isSuperAdmin || !isEditing}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                    isSuperAdmin && isEditing
                      ? 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none'
                      : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                  }`}
                  placeholder="Enter email address"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year Graduated</label>
                  <select
                    value={editFormData.year_graduated?.toString() || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, year_graduated: e.target.value ? parseInt(e.target.value, 10) : '' })}
                    disabled={true}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                  >
                    <option value="">{!editFormData.year_graduated ? 'Not specified' : 'Select Year'}</option>
                    {graduationYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                  <input
                    type="number"
                    value={editFormData.age || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, age: e.target.value })}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                      isEditing ? 'focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-50 text-gray-700 cursor-not-allowed'
                    }`}
                    placeholder={!editFormData.age && !isEditing ? 'Not specified' : 'e.g., 23'}
                    min="15"
                    max="100"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <select
                    value={editFormData.gender || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, gender: e.target.value })}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                      isEditing ? 'focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-50 text-gray-700 cursor-not-allowed'
                    }`}
                  >
                    <option value="">{!isEditing && !editFormData.gender ? 'Not specified' : 'Select gender'}</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Program</label>
                  <select
                    value={editFormData.program || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, program: e.target.value })}
                    disabled={true}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                  >
                    <option value="">{!editFormData.program ? 'Not specified' : 'Select Program'}</option>
                    {getProgramsByDepartment(editFormData.department || '').map((program) => (
                      <option key={program.id} value={program.code || program.name}>
                        {program.full_name || program.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <select
                  value={editFormData.department || ''}
                  onChange={(e) => {
                    const newDepartment = e.target.value
                    setEditFormData({ ...editFormData, department: newDepartment, program: '' })
                  }}
                  disabled={true}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                >
                  <option value="">{!editFormData.department ? 'Not specified' : 'Select Department'}</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.name}>
                      {dept.name}
                    </option>
                  ))}
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Employment Section Header */}
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Employment Information</h3>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employment Status</label>
                <select
                  value={editFormData.current_employment_status || 'Unemployed'}
                  onChange={(e) => setEditFormData({ ...editFormData, current_employment_status: e.target.value })}
                  disabled={!isEditing}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                    isEditing ? 'focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-50 text-gray-700 cursor-not-allowed'
                  }`}
                >
                  {EMPLOYMENT_STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>

              {/* Job-related fields: only shown when employed/self-employed */}
              {isEmployed(editFormData.current_employment_status) && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Position</label>
                    <input
                      type="text"
                      value={editFormData.job_position || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, job_position: e.target.value })}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                        isEditing ? 'focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-50 text-gray-500 cursor-not-allowed'
                      }`}
                      placeholder="e.g., Software Engineer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Field of Work / Company</label>
                    <input
                      type="text"
                      value={editFormData.field_of_work || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, field_of_work: e.target.value })}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                        isEditing ? 'focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-50 text-gray-500 cursor-not-allowed'
                      }`}
                      placeholder={!editFormData.field_of_work && !isEditing ? 'Not specified' : 'e.g., Tech Industry, ABC Company'}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Employer Type</label>
                      <select
                        value={editFormData.employer_type || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, employer_type: e.target.value })}
                        disabled={!isEditing}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                          isEditing ? 'focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-50 text-gray-700 cursor-not-allowed'
                        }`}
                      >
                        <option value="">{!isEditing && !editFormData.employer_type ? 'Not specified' : 'Select type'}</option>
                        {EMPLOYER_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Salary Range</label>
                      <select
                        value={editFormData.salary_range || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, salary_range: e.target.value })}
                        disabled={!isEditing}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                          isEditing ? 'focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-50 text-gray-700 cursor-not-allowed'
                        }`}
                      >
                        <option value="">{!isEditing && !editFormData.salary_range ? 'Not specified' : 'Select range'}</option>
                        {SALARY_RANGE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Job Relevance</label>
                      <select
                        value={editFormData.job_relevance || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, job_relevance: e.target.value })}
                        disabled={!isEditing}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                          isEditing ? 'focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-50 text-gray-700 cursor-not-allowed'
                        }`}
                      >
                        <option value="">{!isEditing && !editFormData.job_relevance ? 'Not specified' : 'Select relevance'}</option>
                        {JOB_RELEVANCE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Employment Length</label>
                      <select
                        value={editFormData.employment_length || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, employment_length: e.target.value })}
                        disabled={!isEditing}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                          isEditing ? 'focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-50 text-gray-700 cursor-not-allowed'
                        }`}
                      >
                        <option value="">{!editFormData.employment_length && !isEditing ? 'Not specified' : 'Select duration'}</option>
                        {EMPLOYMENT_LENGTH_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {editFormData.current_employment_status === 'Unemployed' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unemployment Reasons</label>
                  <textarea
                    value={editFormData.unemployment_reasons || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, unemployment_reasons: e.target.value })}
                    disabled={!isEditing}
                    rows={3}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                      isEditing ? 'focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-50 text-gray-700 cursor-not-allowed'
                    }`}
                    placeholder={!editFormData.unemployment_reasons && !isEditing ? 'Not specified' : 'Please specify reasons'}
                  />
                </div>
              )}

              {/* Additional Information Section */}
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Further Studies</label>
                <div className={`space-y-2 p-3 border border-gray-300 rounded-lg ${
                  isEditing ? 'bg-white' : 'bg-gray-50'
                }`}>
                  {(['Yes - Masters Degree', 'Yes - Doctorate', 'Yes - Professional Certification', 'No', 'Planning to'] as const).map((option) => (
                    <label key={option} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="further_studies"
                        checked={Array.isArray(editFormData.further_studies) && editFormData.further_studies[0] === option}
                        onChange={() => {
                          setEditFormData({ 
                            ...editFormData, 
                            further_studies: [option]
                          })
                        }}
                        disabled={!isEditing}
                        className="border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className={isEditing ? 'text-gray-700' : 'text-gray-600'}>{option}</span>
                    </label>
                  ))}
                  {!isEditing && (!editFormData.further_studies || editFormData.further_studies.length === 0) && (
                    <span className="text-gray-500 text-sm italic">Not specified</span>
                  )}
                </div>
              </div>

              <div>
                <SkillsPicker
                  label="Skills Acquired"
                  value={Array.isArray(editFormData.skills_acquired) ? editFormData.skills_acquired : []}
                  onChange={(skills) => setEditFormData({ ...editFormData, skills_acquired: skills })}
                  disabled={!isEditing}
                  placeholder="Search skills or type a custom one…"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Career Progression</label>
                <input
                  type="text"
                  value={Array.isArray(editFormData.career_progression) ? editFormData.career_progression.join(', ') : editFormData.career_progression || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, career_progression: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                  disabled={!isEditing}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                    isEditing ? 'focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-50 text-gray-700 cursor-not-allowed'
                  }`}
                  placeholder={(!editFormData.career_progression || editFormData.career_progression.length === 0) && !isEditing ? 'Not specified' : 'e.g., Junior Dev, Senior Dev, Team Lead (comma-separated)'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Search Methods</label>
                <input
                  type="text"
                  value={Array.isArray(editFormData.job_search_methods) ? editFormData.job_search_methods.join(', ') : editFormData.job_search_methods || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, job_search_methods: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                  disabled={!isEditing}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                    isEditing ? 'focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-50 text-gray-700 cursor-not-allowed'
                  }`}
                  placeholder={(!editFormData.job_search_methods || editFormData.job_search_methods.length === 0) && !isEditing ? 'Not specified' : 'e.g., Online Applications, Referrals (comma-separated)'}
                />
              </div>
              <div className="border-t border-gray-100 pt-4">
                <CareerTimeline alumniId={currentAlumni?.id} canEdit={false} />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setEditModalOpen(false)
                  setCurrentAlumni(null)
                  setIsEditing(false)
                  setSelectedFile(null)
                  setImagePreview('')
                }}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Close
              </button>
              {isEditing && (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(false)
                      setSelectedFile(null)
                      // Reset form to original data
                      const profile = currentAlumni.alumni_profiles || {}
                      const fullName = profile.first_name && profile.last_name 
                        ? `${profile.first_name} ${profile.last_name}`.trim()
                        : currentAlumni.full_name || ''
                      
                      // Convert database enum values to display values
                      const employmentStatus = toDisplayValue(profile.current_employment_status || 'unemployed', 'employment_status')
                      const employerType = toDisplayValue(profile.employer_type || '', 'employer_type')
                      const jobRelevance = toDisplayValue(profile.job_relevance || '', 'job_relevance')
                      
                      setEditFormData({
                        full_name: fullName,
                        email: currentAlumni.email || '',
                        age: profile.age || '',
                        gender: profile.gender || '',
                        year_graduated: profile.year_graduated || '',
                        program: profile.program || '',
                        department: profile.department || '',
                        current_employment_status: employmentStatus,
                        job_position: profile.job_position || '',
                        field_of_work: profile.field_of_work || '',
                        employer_type: employerType,
                        job_relevance: jobRelevance,
                        salary_range: profile.salary_range || '',
                        employment_length: profile.employment_length || '',
                        unemployment_reasons: profile.unemployment_reasons || '',
                        further_studies: profile.further_studies || [],
                        skills_acquired: profile.skills_acquired || [],
                        career_progression: profile.career_progression || [],
                        job_search_methods: profile.job_search_methods || [],
                        profile_picture: profile.profile_picture || '',
                      })
                      setImagePreview(profile.profile_picture || '')
                    }}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateAlumni}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Approve Confirmation Modal */}
      {approveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 text-center mb-2">Approve Alumni</h2>
              <p className="text-gray-600 text-center">
                Approve <span className="font-semibold">{approveModal.name}</span>? They will gain full access to the platform.
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setApproveModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmApprove}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 text-center mb-2">Reject Alumni</h2>
              <p className="text-gray-600 text-center mb-4">
                Rejecting <span className="font-semibold">{rejectModal.name}</span>. Please provide a reason.
              </p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason..."
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
              />
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setRejectModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmReject}
                disabled={!rejectReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && currentAlumni && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 text-center mb-2">Delete Alumni</h2>
              <p className="text-gray-600 text-center mb-6">
                Are you sure you want to delete{' '}
                <span className="font-semibold">
                  {currentAlumni.alumni_profiles?.[0]?.first_name} {currentAlumni.alumni_profiles?.[0]?.last_name}
                </span>
                ? This action cannot be undone.
              </p>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteModalOpen(false)
                  setCurrentAlumni(null)
                }}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ManageAlumni
